import { z } from "zod";
import { DataSource, ProcessingStatus, QualityClass, SessionStatus, StimulusKind, StudyStatus } from "./enums.js";

/** Umbrales de calidad de validación (configurables por estudio). */
export const QualityThresholds = z
  .object({
    /** Error medio máximo (px CSS) para clasificar como `pass`. */
    passMaxMeanErrorPx: z.number().positive().max(2000).default(150),
    /** Error medio máximo (px CSS) para clasificar como `warn`; por encima es `fail`. */
    warnMaxMeanErrorPx: z.number().positive().max(4000).default(250),
    /** % mínimo de muestras dentro del radio ROI de los puntos de validación para `pass`. */
    passMinPercentInRoi: z.number().min(0).max(100).default(50),
    /** Radio (px CSS) alrededor de cada punto de validación. */
    roiRadiusPx: z.number().int().min(10).max(1000).default(150),
    /** Tasa mínima de muestras por segundo durante la validación. */
    minSamplesPerSecond: z.number().min(0).max(200).default(5),
  })
  .strict()
  .refine((t) => t.warnMaxMeanErrorPx >= t.passMaxMeanErrorPx, {
    message: "El umbral de advertencia debe ser mayor o igual al umbral de aprobación",
    path: ["warnMaxMeanErrorPx"],
  });
export type QualityThresholds = z.infer<typeof QualityThresholds>;

export const StudySettings = z
  .object({
    imageDurationMs: z.number().int().min(1000).max(60000).default(5000),
    fixationDurationMs: z.number().int().min(200).max(10000).default(1000),
    randomizeOrder: z.boolean().default(false),
    /** 0 = sin revalidación intermedia. */
    revalidateEveryNTrials: z.number().int().min(0).max(100).default(5),
    calibrationPoints: z.literal(9).default(9),
    validationPoints: z.number().int().min(5).max(9).default(5),
    /** Calidad mínima de la validación para iniciar sin autorización manual. */
    minCalibrationQuality: z.enum(["pass", "warn"]).default("pass"),
    /** Fracción mínima de muestras válidas (dentro de imagen) por ensayo para entrar al análisis. */
    minimumValidSampleRatio: z.number().min(0).max(1).default(0.5),
    /** Mínimo absoluto de muestras válidas por ensayo para entrar al análisis. */
    minimumValidSamples: z.number().int().min(1).max(1000).default(10),
    /** Permite continuar con baja calidad con autorización auditada del investigador. */
    allowManualOverride: z.boolean().default(true),
    /** Muestra una práctica opcional con una imagen neutra (no se guarda). */
    practiceEnabled: z.boolean().default(true),
    /** Videos con sonido (requiere prueba de audio antes de empezar). */
    videoAudio: z.boolean().default(false),
    /**
     * Validación con punto en movimiento tras la primera calibración: mide el retraso y el error
     * de cada participante (útil para corregir la latencia en el análisis de video).
     */
    pursuitValidation: z.boolean().default(false),
    deleteAfterDays: z.number().int().min(1).max(3650).default(180),
    /** Texto de contacto mostrado en el consentimiento. */
    contactInfo: z.string().max(500).default("Contacta a la persona responsable del estudio."),
    /** Responsable del tratamiento de datos mostrado en el consentimiento. */
    dataController: z.string().max(300).default("Equipo de investigación responsable del estudio"),
    quality: QualityThresholds.default({
      passMaxMeanErrorPx: 150,
      warnMaxMeanErrorPx: 250,
      passMinPercentInRoi: 50,
      roiRadiusPx: 150,
      minSamplesPerSecond: 5,
    }),
  })
  .strict();
export type StudySettings = z.infer<typeof StudySettings>;
export type StudySettingsInput = z.input<typeof StudySettings>;

export const DEFAULT_STUDY_SETTINGS: StudySettings = StudySettings.parse({});

export const StudyGroupInput = z
  .object({
    code: z
      .string()
      .min(1)
      .max(40)
      .regex(/^[a-z0-9_-]+$/, "Usa minúsculas, números, guion o guion bajo"),
    name: z.string().min(1).max(120),
    minAge: z.number().int().min(0).max(130),
    maxAge: z.number().int().min(0).max(130),
    sortOrder: z.number().int().min(0).max(1000).default(0),
  })
  .strict()
  .refine((g) => g.maxAge >= g.minAge, {
    message: "La edad máxima debe ser mayor o igual a la mínima",
    path: ["maxAge"],
  });
export type StudyGroupInput = z.infer<typeof StudyGroupInput>;

export const StudyGroupPatch = z
  .object({
    code: z
      .string()
      .min(1)
      .max(40)
      .regex(/^[a-z0-9_-]+$/)
      .optional(),
    name: z.string().min(1).max(120).optional(),
    minAge: z.number().int().min(0).max(130).optional(),
    maxAge: z.number().int().min(0).max(130).optional(),
    sortOrder: z.number().int().min(0).max(1000).optional(),
  })
  .strict();
export type StudyGroupPatch = z.infer<typeof StudyGroupPatch>;

/**
 * Grupos predeterminados: décadas contiguas de personas adultas, nombradas solo por su rango
 * (sin etiquetas de edad). No se incluyen menores de edad: requerirían consentimiento de
 * madre/padre/tutor, que el flujo de consentimiento actual no contempla.
 */
export const DEFAULT_GROUPS: StudyGroupInput[] = [
  { code: "edad-20-29", name: "20–29 años", minAge: 20, maxAge: 29, sortOrder: 0 },
  { code: "edad-30-39", name: "30–39 años", minAge: 30, maxAge: 39, sortOrder: 1 },
  { code: "edad-40-49", name: "40–49 años", minAge: 40, maxAge: 49, sortOrder: 2 },
];

export const StudyCreate = z
  .object({
    title: z.string().min(1).max(200),
    description: z.string().max(5000).default(""),
    protocol: z.string().max(20000).default(""),
    consentVersion: z.string().min(1).max(40).default("v1"),
    consentText: z.string().max(20000).optional(),
    settings: StudySettings.default(DEFAULT_STUDY_SETTINGS),
    /** Obsoleto: los estudios combinan imágenes y videos. Se acepta por compatibilidad y se ignora. */
    stimulusKind: StimulusKind.optional(),
    /** Si se omite se crean los grupos predeterminados 20–29, 30–39 y 40–49. */
    groups: z.array(StudyGroupInput).min(2).max(12).optional(),
  })
  .strict();
export type StudyCreate = z.infer<typeof StudyCreate>;
export type StudyCreateInput = z.input<typeof StudyCreate>;

export const StudyPatch = z
  .object({
    title: z.string().min(1).max(200).optional(),
    description: z.string().max(5000).optional(),
    protocol: z.string().max(20000).optional(),
    consentVersion: z.string().min(1).max(40).optional(),
    consentText: z.string().max(20000).optional(),
    settings: StudySettings.optional(),
    status: z.enum(["draft", "archived"]).optional(),
  })
  .strict();
export type StudyPatch = z.infer<typeof StudyPatch>;

export interface StudyGroupDto {
  id: string;
  studyId: string;
  code: string;
  name: string;
  minAge: number;
  maxAge: number;
  sortOrder: number;
}

export interface StudyImageDto {
  id: string;
  studyId: string;
  kind: StimulusKind;
  filenameOriginal: string;
  mimeType: string;
  sha256: string;
  naturalWidth: number;
  naturalHeight: number;
  sizeBytes: number;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  /** Número de ensayos guardados que usan esta imagen. */
  trialCount: number;
  /** Solo videos: duración, cuadros por segundo y audio del archivo ya convertido. */
  durationMs: number | null;
  fps: number | null;
  hasAudio: boolean | null;
  /** Las imágenes siempre están `ready`; los videos pasan por `processing`. */
  processingStatus: ProcessingStatus;
  processingError: string | null;
  /** Diferencias respecto a lo recomendado (el archivo se aceptó igualmente). */
  mediaWarnings: string[];
  hasPoster: boolean;
}

export interface StudySummaryDto {
  id: string;
  code: string;
  title: string;
  description: string;
  status: StudyStatus;
  createdAt: string;
  updatedAt: string;
  /** Total de estímulos (imágenes + videos). */
  imageCount: number;
  /** De ellos, videos. */
  videoCount: number;
  groupCount: number;
  sessionCounts: Record<DataSource, { total: number; completed: number }>;
  /** Advertencias de calidad (validaciones `fail`/`warn` u overrides). */
  qualityWarnings: number;
}

export interface StudyDto extends StudySummaryDto {
  protocol: string;
  consentVersion: string;
  consentText: string | null;
  settings: StudySettings;
  groups: StudyGroupDto[];
}

export interface SessionSummaryDto {
  id: string;
  participantCode: string;
  groupId: string | null;
  groupCode: string | null;
  ageYears: number;
  visionCorrection: string;
  selfReportedVision: string;
  source: DataSource;
  status: SessionStatus;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  failureReason: string | null;
  trialsCompleted: number;
  trialsInterrupted: number;
  /** Clasificación de la última validación y si hubo override. */
  lastCalibration: { classification: QualityClass; manualOverride: boolean; meanErrorPx: number | null } | null;
  excluded: { reason: string; createdAt: string } | null;
}

/** Detecta solapamientos de rangos de edad (enteros, inclusivos). Devuelve pares en conflicto. */
export function findAgeOverlaps(
  groups: ReadonlyArray<{ code: string; minAge: number; maxAge: number }>,
): Array<[string, string]> {
  const conflicts: Array<[string, string]> = [];
  for (let i = 0; i < groups.length; i++) {
    for (let j = i + 1; j < groups.length; j++) {
      const a = groups[i]!;
      const b = groups[j]!;
      if (a.minAge <= b.maxAge && b.minAge <= a.maxAge) conflicts.push([a.code, b.code]);
    }
  }
  return conflicts;
}

/** Asigna una edad entera a un grupo. Devuelve `null` si ninguno la contiene o si es ambigua. */
export function assignAgeGroup<G extends { minAge: number; maxAge: number }>(
  groups: ReadonlyArray<G>,
  ageYears: number,
): G | null {
  if (!Number.isInteger(ageYears)) return null;
  const matches = groups.filter((g) => ageYears >= g.minAge && ageYears <= g.maxAge);
  return matches.length === 1 ? matches[0]! : null;
}

export interface CalibrationRunDto {
  id: string;
  sequence: number;
  phase: "initial" | "revalidation";
  startedAt: string;
  endedAt: string;
  classification: QualityClass;
  decision: "continue" | "recalibrate" | "override" | "cancel";
  manualOverride: boolean;
  overrideReason: string | null;
  metrics: import("./participant.js").CalibrationMetrics;
}

export interface TrialSummaryDto {
  id: string;
  imageId: string;
  sequenceIndex: number;
  startedAt: string;
  durationActualMs: number;
  status: "completed" | "interrupted" | "discarded";
  interruptionReason: string | null;
  sampleCountRaw: number;
  sampleCountValid: number;
  sampleCountOnImage: number;
  bounds: import("./participant.js").ImageBoundsDto;
  video: import("./video.js").VideoPlaybackMeta | null;
}

export interface SessionDetailDto extends SessionSummaryDto {
  consentVersion: string;
  consentedAt: string;
  deviceMeta: import("./participant.js").DeviceMeta;
  orderSeed: string;
  imageOrder: string[];
  calibrations: CalibrationRunDto[];
  pursuit: import("./video.js").PursuitRunDto | null;
  trials: TrialSummaryDto[];
}
